import {
  Badge,
  Box,
  BoxHeader,
  BoxSection,
  DialogBody,
  DialogFooter,
  Icon,
  IconButton,
  MultiSelect,
  SearchButtonInput,
  Tooltip,
} from '@civilgrid/ui/components';
import { exportFileStem } from '@civilgrid/utils/src/extract/types';
import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { downloadCSV } from '~/common/miscUtils';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Notify } from '~/components2/Notify';
import { type AssetType, assetTypeColor } from '../common/assetType';
import store from '../store';
import {
  type AgencyContactCard,
  filterAgencyContactCards,
  getAgencyAssetTypes,
  getAgencyContactCards,
  getAgencyContactsCsv,
} from './utility';

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const countEmails = (cards: AgencyContactCard[]) => cards.reduce((sum, card) => sum + card.emails.length, 0);

function AssetTypeBadges({ assetTypes }: { assetTypes: string[] }) {
  return assetTypes.map((type) => (
    <Badge key={type} color={assetTypeColor[type as AssetType] || 'red'}>
      <span className="truncate">{type}</span>
    </Badge>
  ));
}

function ContactCard({ card, projectId }: { card: AgencyContactCard; projectId: string }) {
  const copyEmail = (email: string, agencyIds: string[]) => {
    navigator.clipboard.writeText(email).then(() => {
      Notify.info({ message: `Copied ${email}` });
      captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesContactEmailCopied, {
        project_id: projectId,
        agency_ids: agencyIds,
      });
    });
  };

  return (
    <Box rounded="lg" className="flex-none" data-testid="utilities-contacts-agency-card">
      <BoxHeader variant="bar">
        <h3 className="text-sm font-semibold text-grey-90">{card.agencyName}</h3>
        <AssetTypeBadges assetTypes={card.assetTypes} />
      </BoxHeader>
      <div className="w-full">
        {card.emails.map(({ email, name, agencyIds }) => (
          <BoxSection key={email} variant="row" className="flex-col gap-0.5">
            <div className={name ? 'text-sm font-medium text-grey-90' : 'text-sm text-grey-60'}>
              {name || 'General inbox'}
            </div>
            <div className="flex min-w-0 items-center gap-1.5 text-sm">
              <a
                href={`mailto:${email}`}
                className="focus-ring rounded-sm text-blue-50 [overflow-wrap:anywhere] hover:underline"
                onClick={() =>
                  captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesContactEmailClicked, {
                    project_id: projectId,
                    agency_ids: agencyIds,
                  })
                }
                data-testid="utilities-contacts-email-link"
              >
                {email}
              </a>
              <Tooltip asChild text="Copy email">
                <IconButton
                  variant="hollow"
                  size="icon"
                  icon="copy"
                  aria-label={`Copy email ${email}`}
                  className="text-grey-60 hover:text-grey-90"
                  onClick={() => copyEmail(email, agencyIds)}
                  data-testid="utilities-contacts-copy-email"
                />
              </Tooltip>
            </div>
          </BoxSection>
        ))}
      </div>
    </Box>
  );
}

function NoContactCard({ cards }: { cards: AgencyContactCard[] }) {
  return (
    <Box rounded="lg" className="flex-none" data-testid="utilities-contacts-no-contact-card">
      <BoxHeader variant="bar">
        <Icon icon="infoCircled" size={14} className="flex-none text-primary-dark" />
        <h3 className="text-sm font-semibold text-grey-90">
          No contact on file for {plural(cards.length, 'agency', 'agencies')}
        </h3>
      </BoxHeader>
      <BoxSection variant="row">
        <p className="text-xs text-grey-60">
          Some agencies only take requests through a records portal, web form, or phone. CivilGrid is handling the
          outreach for you.
        </p>
      </BoxSection>
      <div className="w-full">
        {cards.map((card) => (
          <BoxSection key={card.agencyName} variant="row" className="items-center justify-between gap-3 py-2 text-sm">
            <span>{card.agencyName}</span>
            <span className="flex flex-wrap justify-end gap-1">
              <AssetTypeBadges assetTypes={card.assetTypes} />
            </span>
          </BoxSection>
        ))}
      </div>
    </Box>
  );
}

function ZeroState() {
  return (
    <Box
      rounded="lg"
      className="items-center bg-secondary-light/30 px-6 py-8 text-center"
      data-testid="utilities-contacts-zero-state"
    >
      <span className="mb-3 grid size-11 place-items-center rounded-full bg-grey-20 text-grey-90" aria-hidden="true">
        <Icon icon="envelopeClosed" size={20} />
      </span>
      <h3 className="mb-1 text-sm font-semibold text-grey-90">No contacts on file</h3>
      <p className="max-w-[460px] text-sm text-balance text-grey-70">
        Some agencies only take requests through a records portal, web form, or phone. CivilGrid is handling the
        outreach for you.
      </p>
    </Box>
  );
}

/** The Contacts modal's search, Type filter, agency cards, and footer, or a zero state when there are no contacts. */
export default function AgencyContactCards() {
  const { project, agencies } = useSnapshot(store) as typeof store;
  const [search, setSearch] = useState('');
  const [assetTypes, setAssetTypes] = useState<string[]>([]);
  const allCards = useMemo(() => getAgencyContactCards(agencies), [agencies]);
  const typeOptions = useMemo(
    () =>
      getAgencyAssetTypes([...allCards.withContacts, ...allCards.withoutContacts]).map((type) => ({
        value: type,
        label: type,
      })),
    [allCards],
  );
  const { withContacts, withoutContacts } = useMemo(
    () => filterAgencyContactCards(allCards, search, assetTypes),
    [allCards, search, assetTypes],
  );
  const projectId = project?.id ?? '';
  const isEmpty = allCards.withContacts.length === 0;
  const emailCount = countEmails(withContacts);

  // Downloads what the search and Type filter show; agencies with no contact on file are left out.
  const onDownload = () => {
    downloadCSV(getAgencyContactsCsv(withContacts), `${exportFileStem(project?.name, projectId)}_Utility_Contacts.csv`);
    captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesContactsDownloaded, {
      project_id: projectId,
      contact_count: emailCount,
      agency_count: withContacts.length,
      filtered: Boolean(search.trim()) || assetTypes.length > 0,
    });
  };

  const downloadButton = (
    <IconButton
      variant="secondary"
      size="small"
      icon="download"
      disabled={emailCount === 0}
      onClick={onDownload}
      data-testid="utilities-contacts-download-csv"
    >
      Download CSV
    </IconButton>
  );

  // SearchButtonInput debounces onChange, so this fires once typing pauses.
  const onSearchChange = (value: string) => {
    if (value === search) return;
    setSearch(value);
    if (value.trim()) {
      captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesContactsSearched, {
        project_id: projectId,
        query_length: value.trim().length,
        contact_count: countEmails(filterAgencyContactCards(allCards, value, assetTypes).withContacts),
      });
    }
  };

  const onAssetTypesChange = (next: string[]) => {
    setAssetTypes(next);
    captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesContactsTypeFilterChanged, {
      project_id: projectId,
      asset_types: next,
      contact_count: countEmails(filterAgencyContactCards(allCards, search, next).withContacts),
    });
  };

  useEffect(() => {
    if (project?.id) {
      capturePosthogEvent(POSTHOG_EVENTS.civilsearchUtilityContactsViewed, {
        project_id: project.id,
        source: 'contacts_button',
      });
    }
  }, [project?.id]);

  useEffect(() => {
    if (project?.id && isEmpty) {
      captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesContactsZeroStateViewed, { project_id: project.id });
    }
  }, [project?.id, isEmpty]);

  if (isEmpty) {
    return (
      <>
        <DialogBody
          className="mt-3.5 border-t border-grey-20 px-5 pb-4.5 pt-3.5"
          data-testid="utilities-contacts-dialog-body"
        >
          <ZeroState />
        </DialogBody>
        <DialogFooter className="justify-end border-t border-grey-20 px-5 py-3">{downloadButton}</DialogFooter>
      </>
    );
  }

  const hasMatches = withContacts.length > 0 || withoutContacts.length > 0;
  const summary = [
    `${plural(emailCount, 'contact', 'contacts')} at ${plural(withContacts.length, 'agency', 'agencies')}`,
    withoutContacts.length > 0 && `${plural(withoutContacts.length, 'agency has', 'agencies have')} no contact`,
  ]
    .filter(Boolean)
    .join(' · ');
  // Names the search and types too, so a new search with the same counts still changes the text and is announced.
  const announcement = [
    summary,
    search.trim() && `for "${search.trim()}"`,
    assetTypes.length > 0 && `in ${assetTypes.join(', ')}`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className="mt-3.5 flex items-center gap-2 px-5">
        <SearchButtonInput
          keepExpanded
          value={search}
          onChange={onSearchChange}
          placeholder="Search contacts"
          aria-label="Search contacts"
          className="h-7 w-auto min-w-0 flex-1"
          data-testid="utilities-contacts-search"
        />
        <MultiSelect
          aria-label="Asset type"
          options={typeOptions}
          value={assetTypes}
          onValueChange={onAssetTypesChange}
          placeholder="All types"
          countNoun="types"
          triggerClassName="w-[200px] flex-none"
          data-testid="utilities-contacts-type-filter"
        />
      </div>
      <DialogBody
        className="mt-3.5 flex flex-col gap-2.5 border-t border-grey-20 bg-grey-10 px-5 pb-4.5 pt-3.5"
        data-testid="utilities-contacts-dialog-body"
      >
        {withContacts.map((card) => (
          <ContactCard key={card.agencyName} card={card} projectId={projectId} />
        ))}
        {withoutContacts.length > 0 && <NoContactCard cards={withoutContacts} />}
        {!hasMatches && (
          <p className="p-8 text-center text-sm text-grey-70" data-testid="utilities-contacts-no-match">
            No contacts match.
          </p>
        )}
      </DialogBody>
      <DialogFooter className="justify-between border-t border-grey-20 px-5 py-3">
        <span className="text-xs text-grey-60" data-testid="utilities-contacts-summary">
          {summary}
        </span>
        <output aria-live="polite" className="sr-only" data-testid="utilities-contacts-announcement">
          {announcement}
        </output>
        {downloadButton}
      </DialogFooter>
    </>
  );
}
